import { PencilIcon, PlusIcon } from '@heroicons/react/16/solid';
import React from 'react';

import { Badge } from '@/components/ui/catalyst/badge';
import { Switch } from '@/components/ui/catalyst/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import type { BaseWorkflow, WorkflowRowBaseProps } from '../types';
import { NotesEditor } from './NotesEditor';
import { useNotesEditor } from './useNotesEditor';

// -----------------------------------------------------------------------------
// Guideline Context Configuration (Single Source of Truth)
// -----------------------------------------------------------------------------

/**
 * Context type for guideline text configuration.
 * Determines all guideline-related UI text in a single prop.
 */
export type GuidelineContext = 'firm' | 'case';

/**
 * Centralized configuration for guideline text by context.
 * Add new contexts here to extend functionality.
 */
const GUIDELINE_CONFIG: Record<
  GuidelineContext,
  {
    addButtonText: string;
    editButtonText: string;
    placeholder: string;
    displayLabel: string;
  }
> = {
  firm: {
    addButtonText: 'Add firm guideline',
    editButtonText: 'Edit firm guideline',
    placeholder: 'Add firm guideline...',
    displayLabel: 'Firm Guideline',
  },
  case: {
    addButtonText: 'Add case guideline',
    editButtonText: 'Edit case guideline',
    placeholder: 'Add case guideline...',
    displayLabel: 'Case Guideline',
  },
};

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

interface WorkflowRowBaseComponentProps extends WorkflowRowBaseProps {
  /** Workflow data from API */
  workflow: BaseWorkflow;
  /** Context determines all guideline-related text (firm vs case) */
  guidelineContext: GuidelineContext;
  /** When true, toggle is view-only but notes remain editable (for inherited settings) */
  toggleReadOnly?: boolean;
  /** Optional additional content (e.g., config editor for firm) */
  children?: React.ReactNode;
}

/**
 * Shared base component for workflow rows.
 *
 * Provides consistent UI for:
 * - Workflow name and status badge
 * - Enable/disable toggle
 * - Description text
 * - Notes editing with async save
 *
 * Used by both firm and matter workflow rows.
 * Firm extends this with WorkflowConfigEditor.
 */
export const WorkflowRowBase: React.FC<WorkflowRowBaseComponentProps> = ({
  workflow,
  serviceDisabled,
  disabled,
  onToggle,
  onNotesChange,
  guidelineContext,
  toggleReadOnly,
  children,
}) => {
  // Derive all text from context config (single source of truth)
  const guidelineConfig = GUIDELINE_CONFIG[guidelineContext];
  const isWorkflowDisabled = disabled || serviceDisabled;
  // When toggleReadOnly, toggle is disabled but notes remain editable based only on workflow state
  const isToggleDisabled = isWorkflowDisabled || toggleReadOnly;
  const isNotesDisabled = toggleReadOnly
    ? disabled || !workflow.is_enabled
    : isWorkflowDisabled || !workflow.is_enabled;

  const notesEditor = useNotesEditor({
    initialNotes: workflow.notes,
    onSave: onNotesChange,
  });

  // Show inline guideline button when workflow is enabled and not currently editing
  const showInlineGuidelineButton =
    workflow.is_enabled && !notesEditor.isEditing;
  const hasExistingNotes = !!workflow.notes;

  return (
    <div className="px-3 py-1.5">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          {/* Header: Name + Toggle */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-medium text-zinc-950 dark:text-white">
                {workflow.display_name}
              </span>
              {workflow.status === 'coming_soon' && (
                <Badge color="zinc" className="text-[10px] py-0 px-1">
                  Not Live
                </Badge>
              )}
              {showInlineGuidelineButton && (
                <button
                  type="button"
                  onClick={notesEditor.startEditing}
                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[11px] font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 dark:text-blue-400 dark:bg-blue-950 dark:hover:bg-blue-900 rounded transition-colors"
                >
                  {hasExistingNotes ? (
                    <>
                      <PencilIcon className="h-3 w-3" />
                      {guidelineConfig.editButtonText}
                    </>
                  ) : (
                    <>
                      <PlusIcon className="h-3 w-3" />
                      {guidelineConfig.addButtonText}
                    </>
                  )}
                </button>
              )}
            </div>
            {toggleReadOnly ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Switch
                      checked={workflow.is_enabled}
                      onChange={onToggle}
                      disabled={isToggleDisabled}
                      color="blue"
                    />
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  Scope is set by firm. Add case guidelines or edit tasks to
                  customize.
                </TooltipContent>
              </Tooltip>
            ) : (
              <Switch
                checked={workflow.is_enabled}
                onChange={onToggle}
                disabled={isToggleDisabled}
                color="blue"
              />
            )}
          </div>

          {/* Description */}
          {workflow.description && (
            <p className="text-[11px] leading-tight text-zinc-500 dark:text-zinc-400">
              {workflow.description}
            </p>
          )}

          {/* Task Types - show which tasks this workflow applies to */}
          {workflow.task_types && workflow.task_types.length > 0 && (
            <p className="text-[11px] text-zinc-400 dark:text-zinc-500 mt-0.5">
              <span className="font-medium">Tasks:</span>{' '}
              {workflow.task_types.join(', ')}
            </p>
          )}

          {/* Optional config section (passed as children) */}
          {children}

          {/* Notes section - only shows when editing or has existing notes */}
          <NotesEditor
            workflowCode={workflow.workflow_code}
            notes={workflow.notes}
            notesValue={notesEditor.notesValue}
            isEditing={notesEditor.isEditing}
            isSaving={notesEditor.isSaving}
            disabled={isNotesDisabled}
            isWorkflowDisabled={!workflow.is_enabled}
            placeholder={guidelineConfig.placeholder}
            displayLabel={guidelineConfig.displayLabel}
            onNotesValueChange={notesEditor.setNotesValue}
            onSave={notesEditor.saveNotes}
            onCancel={notesEditor.cancelEditing}
          />
        </div>
      </div>
    </div>
  );
};
